import React, { useState } from "react";
import { Box, Typography, Tooltip, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ResizeHandle } from "../../../../../components/common/ResizeColumns";
import { updatePipStatus } from "../../../../../api/scgPool";
import useAuth from "../../../../../hooks/useAuth";
import { ROLES } from "../../../../../constants/auth";
import {
  PIP_UPDATE_FAILED_MESSAGE,
  EXIT_NOTICE_WINDOW_MONTHS,
  EXIT_LABEL_RESIGNED,
  EXIT_LABEL_CONTRACT_ENDS,
} from "./constants";

// Parsed as a local date so a "YYYY-MM-DD" value isn't shifted by timezone.
const isWithinExitWindow = (dateStr) => {
  if (!dateStr) return false;
  const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
  const exitDate = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const windowEnd = new Date(today);
  windowEnd.setMonth(windowEnd.getMonth() + EXIT_NOTICE_WINDOW_MONTHS);
  return exitDate >= today && exitDate <= windowEnd;
};

const getInitials = (name) => {
  if (!name) return "??";
  const parts = name.trim().split(" ").filter(Boolean);
  return parts.length === 1
    ? parts[0].slice(0, 2).toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = ["#28B3B4", "#D6DF27", "#4a9960", "#7a5abd", "#c88a3a"];
const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const Avatar = ({ name, initials }) => {
  const color = getAvatarColor(name);
  const display = initials || getInitials(name);
  return (
    <Box
      sx={{
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        flexShrink: 0,
        background: `${color}22`,
        border: `1px solid ${color}55`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'DM Mono', monospace",
        fontSize: "10px",
        fontWeight: 700,
        color,
      }}
    >
      {display}
    </Box>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleString("en-GB", { month: "short" });
  const yr = String(d.getFullYear()).slice(2);
  return `${day} ${mon} ${yr}`;
};

// Resignation styling — kept local/inline to match the existing PendingChip pattern
// rather than pulling new tokens into the shared theme.
const RESIGNED_RED = "#E05A5A";
const RESIGNED_BG = "rgba(224,90,90,0.07)";
const RESIGNED_BORDER = "rgba(224,90,90,0.32)";

// The GRID columns — must match header exactly:
// [avatar 44px] [name 200px] [level 130px] [skills 160px] [free-capacity 160px] [allocations 1fr] [days 110px]


const FTEBar = ({ freeFte, t }) => {
  const free = Math.max(0, Math.min(1, freeFte ?? 0));
  const occupied = 1 - free;
  const freePct = Math.round(free * 100);
  const occupiedPct = Math.round(occupied * 100);
  const freeColor = free >= 0.75 ? t.accent : t.gold;

  return (
    <Box>
      <Box
        sx={{
          height: "5px",
          borderRadius: "3px",
          background: t.surface3,
          overflow: "hidden",
          mb: "4px",
          width: "100px",
        }}
      >
        <Box sx={{ height: "100%", display: "flex" }}>
          {occupiedPct > 0 && (
            <Box
              sx={{
                width: `${occupiedPct}%`,
                background: t.border3,
                borderRadius: freePct === 0 ? "3px" : "3px 0 0 3px",
              }}
            />
          )}
          {freePct > 0 && (
            <Box
              sx={{
                width: `${freePct}%`,
                background: freeColor,
                borderRadius: occupiedPct === 0 ? "3px" : "0 3px 3px 0",
              }}
            />
          )}
        </Box>
      </Box>
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "10px",
          color: freeColor,
          fontWeight: 600,
        }}
      >
        {free.toFixed(1)} free
      </Typography>
    </Box>
  );
};

const MiniBar = ({ fte, t }) => {
  const val = Math.max(0, Math.min(1, fte ?? 0));
  const pct = Math.round(val * 100);
  const color = val >= 0.5 ? t.accent : t.gold;
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}
    >
      <Box
        sx={{
          width: "48px",
          height: "4px",
          borderRadius: "2px",
          background: t.surface3,
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            background: color,
            borderRadius: "2px",
          }}
        />
      </Box>
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "10px",
          color,
          fontWeight: 600,
        }}
      >
        {val.toFixed(1)}
      </Typography>
    </Box>
  );
};

const AllocationChip = ({ alloc, t }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "5px 10px",
      background: t.surface2,
      border: `1px solid ${t.border}`,
      borderRadius: "3px",
      gap: "8px",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "10px",
        color: t.accent,
        fontWeight: 500,
        flexShrink: 0,
        whiteSpace: "nowrap",
        maxWidth: "110px",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {alloc.positionCode}
    </Typography>
    <Typography
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontSize: "11px",
        fontWeight: 600,
        color: t.text,
        flexShrink: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {alloc.roleTitle}
    </Typography>
    <Typography
      sx={{
        fontFamily: "'Inter', sans-serif",
        fontSize: "10px",
        color: t.text2,
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      {alloc.macName}
    </Typography>
    <MiniBar fte={alloc.fte} t={t} />
  </Box>
);

const PendingChip = ({ req, t }) => (
  <Box sx={{
    display: "flex", alignItems: "center",
    padding: "5px 10px",
    background: `rgba(214,223,39,0.06)`,
    border: `1px solid rgba(214,223,39,0.22)`,
    borderRadius: "3px", gap: "8px", minWidth: 0,
  }}>
    <Box sx={{
      padding: "1px 6px", borderRadius: "2px", flexShrink: 0,
      background: "rgba(214,223,39,0.1)", border: "1px solid rgba(214,223,39,0.3)",
    }}>
      <Typography sx={{
        fontFamily: "'DM Mono', monospace", fontSize: "8px",
        color: t.gold, fontWeight: 600, whiteSpace: "nowrap",
      }}>
        {req.status === "PMO_APPROVED" ? "AT SCG" : "PENDING PMO"}
      </Typography>
    </Box>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace", fontSize: "10px",
      color: t.accent, flexShrink: 0, whiteSpace: "nowrap",
      maxWidth: "110px", overflow: "hidden", textOverflow: "ellipsis",
    }}>
      {req.positionCode}
    </Typography>
    <Typography sx={{
      fontFamily: "'Inter', sans-serif", fontSize: "11px",
      fontWeight: 600, color: t.text, flexShrink: 1,
      minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    }}>
      {req.roleTitle}
    </Typography>
    <Typography sx={{
      fontFamily: "'Inter', sans-serif", fontSize: "10px",
      color: t.text2, flexShrink: 0, whiteSpace: "nowrap",
    }}>
      {req.macName}
    </Typography>
    <MiniBar fte={req.fte} t={t} />
  </Box>
);

// Shown in the "Active Allocations" column when the associate has an
// LWD for Psi360 set — i.e. they've resigned and are serving notice.
const ServingNoticeChip = () => (
  <Box sx={{
    display: "inline-flex", alignItems: "center",
    padding: "3px 9px",
    background: RESIGNED_BG,
    border: `1px solid ${RESIGNED_BORDER}`,
    borderRadius: "3px",
  }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace", fontSize: "10px",
      fontWeight: 700, letterSpacing: "0.04em",
      color: RESIGNED_RED, whiteSpace: "nowrap",
    }}>
      SERVING NOTICE
    </Typography>
  </Box>
);

// Shown in the "Pending Requests" column when the exit date is within the
// notice window — "Resigned" for employees, "Contract ends" for contractors.
const ResignedChip = ({ label, lwd }) => (
  <Box sx={{
    display: "inline-flex", alignItems: "center",
    padding: "5px 10px",
    background: RESIGNED_BG,
    border: `1px solid ${RESIGNED_BORDER}`,
    borderRadius: "3px", gap: "8px",
  }}>
    <Typography sx={{
      fontFamily: "'Inter', sans-serif", fontSize: "11px",
      fontWeight: 600, color: RESIGNED_RED, whiteSpace: "nowrap",
    }}>
      {label}
    </Typography>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace", fontSize: "10px",
      color: RESIGNED_RED, whiteSpace: "nowrap",
    }}>
      LWD: {formatDate(lwd)}
    </Typography>
  </Box>
);

const PIP_AMBER = "#D4803A";
const PIP_BG = "rgba(212, 128, 58, 0.08)";
const PIP_BORDER = "rgba(212, 128, 58, 0.35)";

const PipChip = ({ canEdit, onRemove, loading }) => {
  const content = (
    <Box
      onClick={canEdit && !loading ? onRemove : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 8px",
        background: PIP_BG,
        border: `1px solid ${PIP_BORDER}`,
        borderRadius: "3px",
        alignSelf: "flex-start",
        cursor: canEdit ? "pointer" : "default",
        gap: "6px",
        transition: "all 0.15s ease",
        "&:hover": canEdit
          ? { background: "rgba(212, 128, 58, 0.18)", borderColor: PIP_AMBER }
          : undefined,
      }}
    >
      {loading ? (
        <CircularProgress size={9} sx={{ color: PIP_AMBER }} />
      ) : (
        <Typography
          sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "9px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: PIP_AMBER,
            whiteSpace: "nowrap",
          }}
        >
          PIP
        </Typography>
      )}
      {canEdit && !loading && (
        <Typography
          sx={{
            fontSize: "11px",
            lineHeight: 1,
            fontWeight: 700,
            color: PIP_AMBER,
            opacity: 0.7,
            "&:hover": { opacity: 1 },
          }}
        >
          ×
        </Typography>
      )}
    </Box>
  );

  return canEdit ? (
    <Tooltip title="Click to remove from PIP" arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

const AddPipButton = ({ onClick, loading }) => (
  <Tooltip title="Mark associate as PIP" arrow>
    <Box
      onClick={!loading ? onClick : undefined}
      className="add-pip-btn"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 6px",
        background: "transparent",
        border: `1px dashed rgba(212, 128, 58, 0.45)`,
        borderRadius: "3px",
        cursor: "pointer",
        opacity: 0,
        transition: "all 0.15s ease",
        "&:hover": {
          opacity: 1,
          background: PIP_BG,
          borderColor: PIP_AMBER,
        },
      }}
    >
      {loading ? (
        <CircularProgress size={8} sx={{ color: PIP_AMBER }} />
      ) : (
        <Typography
          sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "8.5px",
            fontWeight: 600,
            color: PIP_AMBER,
            whiteSpace: "nowrap",
          }}
        >
          + PIP
        </Typography>
      )}
    </Box>
  </Tooltip>
);

export const ColHeader = ({ label, sortable, active, onClick, t }) => (
  <Typography
    onClick={onClick}
    sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "9px",
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: active ? t.text2 : t.text3,   // brighten when active
      display: "flex",
      alignItems: "center",
      gap: "4px",
      userSelect: "none",
      cursor: sortable ? "pointer" : "default",
    }}
  >
    {label}
  </Typography>
);

export const SkeletonRow = ({ t, cols }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: cols,
      padding: "14px 20px",
      alignItems: "center",
      borderBottom: `1px solid ${t.border}`,
      gap: "8px",
    }}
  >
    {[32, 140, 90, 120, 100, 200, 180, 70].map((w, i) => (
      <Box
        key={i}
        sx={{
          height: "12px",
          borderRadius: "3px",
          width: `${w}px`,
          maxWidth: "100%",
          background: t.surface3,
          opacity: 0.6,
        }}
      />
    ))}
  </Box>
);

// Export GRID so the header row in the parent can import + use the same value
export const POOL_DEFAULT_WIDTHS = [44, 200, 130, 160, 160, 320, 280, 110];

const PoolRow = ({ associate, isLast, gridCols, refreshPool }) => {
  const theme = useTheme();
  const t = theme.custom;

  const { hasAnyRole } = useAuth();
  const isScgUser = hasAnyRole([ROLES.SCG, ROLES.ADMIN]);

  const [isInPip, setIsInPip] = useState(Boolean(associate.isInPip));
  const [loadingPip, setLoadingPip] = useState(false);
  const [pipError, setPipError] = useState(null);

  const handleTogglePip = async (targetStatus) => {
    try {
      setLoadingPip(true);
      setPipError(null);
      await updatePipStatus(associate.associateId, targetStatus);
      setIsInPip(targetStatus);
      if (refreshPool) refreshPool();
    } catch (err) {
      setPipError(err?.response?.data?.error || err?.response?.data?.message || PIP_UPDATE_FAILED_MESSAGE);
    } finally {
      setLoadingPip(false);
    }
  };

  const hasAllocations = associate.activeAllocations?.length > 0;
  // ExitDate is the resignation LWD for employees and the contract end date
  // for contractors, so only dates within the notice window are shown, and
  // "Serving Notice" applies to employees only.
  const showExitDate = isWithinExitWindow(associate.exitDate);
  const isServingNotice = showExitDate && !associate.isContractor;
  return (
    <Box
      sx={{
        borderBottom: isLast ? "none" : `1px solid ${t.border}`,
        transition: "background 0.15s",
        "&:hover": { 
          background: t.surface2,
          "& .add-pip-btn": { opacity: 0.65 },
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridCols,
          padding: "12px 20px",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {/* 1 — Avatar */}
        <Avatar name={associate.fullName} initials={associate.initials} />

        {/* 2 — Name + New badge */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: t.text,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {associate.fullName}
          </Typography>
          {associate.isNew && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                mt: "3px",
                padding: "1px 6px",
                background: t.accent2,
                border: `1px solid ${t.accent}44`,
                borderRadius: "2px",
              }}
            >
              <Box
                sx={{
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  background: t.accent,
                }}
              />
              <Typography
                sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "8px",
                  color: t.accent,
                  letterSpacing: "0.08em",
                }}
              >
                New
              </Typography>
            </Box>
          )}
          {associate.isContractor && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                mt: "3px",
                padding: "0px 5px",
                borderRadius: "2px",
                background: "rgba(214,223,39,0.12)",
                border: "1px solid rgba(214,223,39,0.35)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "7.5px",
                  letterSpacing: "0.06em",
                  fontWeight: 700,
                  color: t.gold,
                }}
              >
                CONTRACTOR
              </Typography>
            </Box>
          )}
        </Box>

        {/* 3 — Level */}
        <Box>
          <Box
            sx={{
              display: "inline-block",
              padding: "2px 8px",
              background: `${t.gold}18`,
              border: `1px solid ${t.gold}44`,
              borderRadius: "3px",
              mb: "3px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "11px",
                fontWeight: 700,
                color: t.gold,
                lineHeight: 1.2,
              }}
            >
              {associate.levelCode}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "10px",
              color: t.text3,
              lineHeight: 1.2,
            }}
          >
            {associate.levelName}
          </Typography>
        </Box>

        {/* 4 — Skills */}
        <Box
          sx={{
            display: "flex",
            gap: "4px",
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          {associate.skills?.length > 0 ? (
            <>
              {associate.skills.slice(0, 3).map((s) => (
                <Box
                  key={s}
                  sx={{
                    padding: "2px 8px",
                    borderRadius: "3px",
                    background: t.surface3,
                    border: `1px solid ${t.border2}`,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "9px",
                    color: t.text2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {s}
                </Box>
              ))}
              {associate.skills.length > 3 && (
                <Box
                  sx={{
                    padding: "2px 8px",
                    borderRadius: "3px",
                    background: t.surface3,
                    border: `1px solid ${t.border2}`,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "9px",
                    color: t.text3,
                  }}
                >
                  +{associate.skills.length - 3}
                </Box>
              )}
            </>
          ) : (
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "11px",
                color: t.text3,
                fontStyle: "italic",
              }}
            >
              —
            </Typography>
          )}
        </Box>

        {/* 5 — Free capacity bar */}
        <FTEBar freeFte={associate.freeFte} t={t} />

        {/* 6 — Active allocations */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0, alignItems: "flex-start" }}>
          {isServingNotice && <ServingNoticeChip />}
          {hasAllocations ? (
            associate.activeAllocations.map((alloc, i) => (
              <AllocationChip key={i} alloc={alloc} t={t} />
            ))
          ) : !isServingNotice ? (
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", color: t.text2, fontStyle: "italic" }}>
              — No active allocations —
            </Typography>
          ) : null}
        </Box>

        {/* 7 — Pending requests / Resignation / PIP */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0, alignItems: "flex-start" }}>
          {showExitDate && (
            <ResignedChip
              label={associate.isContractor ? EXIT_LABEL_CONTRACT_ENDS : EXIT_LABEL_RESIGNED}
              lwd={associate.exitDate}
            />
          )}

          {isInPip ? (
            <PipChip
              canEdit={isScgUser}
              onRemove={() => handleTogglePip(false)}
              loading={loadingPip}
            />
          ) : (
            isScgUser && (
              <AddPipButton
                onClick={() => handleTogglePip(true)}
                loading={loadingPip}
              />
            )
          )}

          {pipError && (
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: "#c84040" }}>
              {pipError}
            </Typography>
          )}

          {associate.pendingRequests?.length > 0 ? (
            associate.pendingRequests.map((req) => (
              <PendingChip key={req.requestId} req={req} t={t} />
            ))
          ) : !showExitDate && !isInPip && !isScgUser ? (
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", color: t.text3, fontStyle: "italic" }}>
              —
            </Typography>
          ) : null}
        </Box>

        {/* 8 — Days in pool */}
        <Box>
          <Typography
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "13px",
              fontWeight: 700,
              color: associate.daysInPool > 30 ? t.gold : t.text2,
              whiteSpace: "nowrap",
            }}
          >
            {associate.daysInPool ?? "—"}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "10px",
              color: t.text3,
              lineHeight: 1.2,
            }}
          >
            days
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PoolRow;
